/**
 * IntroConfig — single visible toggle surface for the /intro URL.
 *
 * /intro is the "first-time character" test surface — what a brand-new player
 * experiences the moment they make a hero: wake in the Waking Vault (tomb_waking),
 * walk the tomb → village → first_field on-ramp, then continue through the
 * Hollow Coil road to the Undercroft climax.
 *
 * The point (DJ, 2026-06-27): a repeatable way to test the intro experience —
 * opening text / dialogue / narration / FTUE — exactly as a first-timer sees it.
 * `resetOnLoad` wipes per-tab state every visit so you ALWAYS arrive as a fresh
 * character (no carried progress, the opening fires from scratch). The class
 * picker IS shown (no autoClass) — making the character is part of the intro.
 *
 * This is the surface to BUILD opening narration against, too: when we add intro
 * text/cutscene/FTUE beats, /intro is where they're authored + QA'd.
 *
 * Wiring (mirrors /adventure):
 *   - applyModeUrlCoercion.ts → coerceModeUrl injects ?map=tomb_waking + preset
 *   - data/levels/tomb_waking.json → Map 1 geometry (registered in LevelRegistry)
 *   - data/levels/village_arrival.json → Map 2 geometry
 *   - data/levels/first_field.json → Map 3 geometry and the road into Hollow Coil
 *   - tidewend → outskirts → drowned road → lanternwood → brackenmoor →
 *     coil_undercroft continues the same authored first-play journey
 *   - content/zones/tomb_waking.toml → empty / mob-free on-ramp Map 1
 *   - content/zones/village_arrival.toml → Tomas + Maria + Elder Aislin
 *   - content/quests/*.toml → first_hunt (the opening loop; combat now in first_field)
 *   - HomeModePicker.tsx → the "New Adventure" card navigates here
 */

export const INTRO_CONFIG = {
  // Fresh start EVERY visit — the whole point is "as if you just made a
  // character," so never carry prior progress into the intro.
  resetOnLoad: true,

  // Gentlest first-time melee identity for the cozy on-ramp (player can change
  // it in the picker — choosing the hero is part of the first-time experience).
  defaultClass: 'warrior' as const,

  // 'live-lobby' = clean game viewport (nameplates + HP bars on, dev UI off) —
  // the actual game's presentation, not a diagnostic surface.
  preset: 'live-lobby' as const,
  skipSplash: true,
  skipLogin: true,

  // Route to the FULL character creator (name + class + appearance), not the
  // quick 6-button picker — "making the character is part of the intro." On
  // create the hero is persisted and spawns in the Waking Vault: the one-card
  // create-a-hero → begin-the-story e2e flow.
  create: true,

  /** Canonical first map for the intro-to-Hollow-Coil journey. */
  startMap: 'tomb_waking' as const,

  // ── Per-map opening narration ───────────────────────────────
  // Each leg owns a short sensory beat. The overlay tracks playback per map,
  // so moving through the complete route reveals each transition without a
  // return visit replaying an earlier beat.
  //
  // Design rules (docs/design/levels/on-ramp-design.md §1):
  //  - 2-4 lines, atmospheric not expository. Sets tone, not lore.
  //  - Skippable — never blocks input. The player can act while it fades.
  //  - Complements Tomas, doesn't duplicate: the last line points the player
  //    toward the shore NPC without naming him or giving the quest.
  narrationByMap: {
    tomb_waking: {
      enabled: true,
      lineDelayMs: 2400,
      holdMs: 5000,
      lines: [
        'You wake on cold stone, the taste of dust and old water in your mouth.',
        'Candles still burn in this forgotten place — someone was here, recently.',
        'A faint green light coils through the cracks above — a way out. Follow it.',
      ] as readonly string[],
    },
    village_arrival: {
      enabled: true,
      lineDelayMs: 2000,
      holdMs: 4000,
      lines: [
        'The village is awake, but no one raises their voice. Hearthlight trembles beneath a watchful moon.',
        'A researcher waits in the square. He keeps glancing toward the fields.',
        'Beyond the last lantern, the road disappears into silver grass.',
      ] as readonly string[],
    },
    first_field: {
      enabled: true,
      lineDelayMs: 1800,
      holdMs: 3000,
      lines: [
        'Moonlight washes the field in a sickly silver. The grass moves when the wind does not.',
        "Pigs root in the dark ahead. Tomas's warning begins to make sense.",
      ] as readonly string[],
    },
    moonfall_hollow: {
      enabled: true,
      lineDelayMs: 2000,
      holdMs: 3600,
      lines: [
        'The high road gives way to a hollow cupped beneath the moon.',
        'Far below, pale grass bends toward a path no wind has touched.',
      ] as readonly string[],
    },
    tidewend: {
      enabled: true,
      lineDelayMs: 1900,
      holdMs: 3400,
      lines: [
        "Tidewend's lamps burn low against the black water. For a moment, the road feels far away.",
        'Beyond the eastern gate, each light stands farther from the next.',
      ] as readonly string[],
    },
    tidewend_outskirts: {
      enabled: true,
      lineDelayMs: 1900,
      holdMs: 3400,
      lines: [
        'The last farmhouse keeps one window lit. Past its fence, the mist has swallowed the road.',
      ] as readonly string[],
    },
    moonfen_pasture: {
      enabled: true,
      lineDelayMs: 2000,
      holdMs: 3600,
      lines: [
        'The pasture falls away beneath a lid of pale fog.',
        'A bell sounds somewhere below, though nothing in the grass is moving.',
      ] as readonly string[],
    },
    drowned_road: {
      enabled: true,
      lineDelayMs: 2000,
      holdMs: 3800,
      lines: [
        'Water lies over the old causeway, perfectly still until something moves beneath it.',
        'Ahead, the lanterns continue where the road does not.',
      ] as readonly string[],
    },
    lanternwood: {
      enabled: true,
      lineDelayMs: 2000,
      holdMs: 3600,
      lines: [
        'The lanterns burn without warmth. Between them, the trees lean toward Brackenmoor.',
      ] as readonly string[],
    },
    // Plan 27 D50: the first big town, reached by the Tidewend ferry after the Coil.
    wickhaven: {
      enabled: true,
      lineDelayMs: 1900,
      holdMs: 3400,
      lines: [
        'Across the water, Wickhaven keeps its lamps lit from the Coil\'s own flame.',
        'Every road out of town begins at a lantern.',
      ] as readonly string[],
    },
    lanternroot_hollow: {
      enabled: true,
      lineDelayMs: 2000,
      holdMs: 3600,
      lines: [
        'Old roots cradle a second road beneath the lanterns.',
        'Their green glass brightens, one by one, as you descend.',
      ] as readonly string[],
    },
    brackenmoor: {
      enabled: true,
      lineDelayMs: 2100,
      holdMs: 4000,
      lines: [
        'The lighthouse rises beyond the moor, dark against the moon.',
        'Its dead beam turns once across the fog, then disappears.',
      ] as readonly string[],
    },
    coil_undercroft: {
      enabled: true,
      lineDelayMs: 2200,
      holdMs: 4600,
      lines: [
        'The stair winds beneath the dead lighthouse. Every broken gallery leans toward the same green pulse.',
        'Below, something keeps the Coil turning long after its light went out.',
        'Descend. End the keeping.',
      ] as readonly string[],
    },
  },
} as const;

export type IntroNarrationMapId = keyof typeof INTRO_CONFIG.narrationByMap;
export type IntroNarrationConfig = (typeof INTRO_CONFIG.narrationByMap)[IntroNarrationMapId];

export function getIntroNarration(mapId: string): IntroNarrationConfig | null {
  return mapId in INTRO_CONFIG.narrationByMap
    ? INTRO_CONFIG.narrationByMap[mapId as IntroNarrationMapId]
    : null;
}

/** Wiped by the /intro reset because every key retains the `helix_*` prefix. */
export function getIntroNarrationPlayedKey(mapId: string): string {
  return `helix_intro_narration_played_${mapId}`;
}
